import { useShallowRouter } from '@components/Hooks/useRouter';
import { updateQueryParams } from '@utils/route';
import {
  Box,
  Tag,
  Heading,
  TagProps,
  Text,
  TextProps,
  HeadingProps,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useCallback } from 'react';
import useTemplateSearchField from '@components/Templates/TemplateSearchField/useTemplateSearchField';
import s from './SearchTermTag.module.scss';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import {
  TEMPLATES_BI_CLICK_TYPES,
  useTemplatesBi,
} from '@components/Hooks/useTemplatesBi';

export const dataHooks = {
  container: 'search-term-tag-container',
  tag: 'search-term-tag-tag',
};

interface SearchTermTagProps {
  tagProps?: Partial<TagProps>;
  className?: string;
  size?: 'normal' | 'large';
}

const SearchTermTag = ({
  tagProps,
  className,
  size = 'normal',
}: SearchTermTagProps) => {
  const {
    state: { filters },
  } = useTemplatesContext();
  const searchFilterValue = filters?.search;
  const { t } = useTranslation();
  const { navigate, router } = useShallowRouter();
  const { isSearchEnabled } = useTemplateSearchField();
  const { logBiClick } = useTemplatesBi({});

  const onRemove = useCallback(() => {
    logBiClick({
      click_type: TEMPLATES_BI_CLICK_TYPES.searchBarMostPopular,
      click_data: 'clear filter',
    });
    navigate(
      updateQueryParams({}, router, {
        search: true,
      }),
    );
  }, [navigate, router, logBiClick]);

  if (!searchFilterValue || !isSearchEnabled) {
    return null;
  }

  const headingProps: HeadingProps = {
    as: 'h2',
    size: size === 'large' ? 'large' : 'medium',
  };

  const textProps: TextProps = {
    size: size === 'large' ? 'medium' : 'small',
    weight: size === 'large' ? 'normal' : 'thin',
  };

  return (
    <Box
      dataHook={dataHooks.container}
      className={`${s.searchTermContainer} ${className ?? ''}`}
      verticalAlign="middle"
    >
      <Box>
        <Heading {...headingProps}>
          {t('marketplace.templates-page.search-tag-label')}
        </Heading>
      </Box>
      <Tag
        dataHook={dataHooks.tag}
        size={size === 'normal' ? 'medium' : 'large'}
        key="searchTag"
        id="search"
        skin="neutral"
        removable={!!onRemove}
        onRemove={onRemove}
        {...tagProps}
      >
        <Text {...textProps}>{searchFilterValue}</Text>
      </Tag>
    </Box>
  );
};

export default SearchTermTag;
